/**
 * Advanced Search Modal Component
 * 
 * Provides advanced search functionality for chat messages with filters:
 * - Text search
 * - Date range
 * - Sender filter
 * - Conversation type filter
 * - Scope filter 
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Modal,
  Input,
  DatePicker,
  Select,
  Button,
  Space,
  Form,
  Empty,
  Spin,
  Typography,
  Divider,
} from 'antd';
import {
  SearchOutlined,
  FilterOutlined,
  HistoryOutlined,
  ClearOutlined,
  CalendarOutlined,
  UserOutlined,
  TeamOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { App } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import type { ChatScope, Message, Conversation } from '@municipality/types';
import { chatService } from '@municipality/chat';
import { SearchResults } from './SearchResults';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

interface AdvancedSearchModalProps {
  open: boolean;
  onClose: () => void;
  currentUserId: string;
  scope: ChatScope;
  municipalityId?: string;
  conversations: Conversation[];
  onJumpToMessage: (conversationId: string, messageId: string, messageDate?: Date) => void;
}

interface SearchFilters {
  query: string;
  dateRange?: [Dayjs, Dayjs];
  senderId?: string;
  conversationType?: 'direct' | 'group';
  conversationIds?: string[];
}

interface SearchHistoryItem {
  query: string;
  filters: {
    dateFrom?: Dayjs;
    dateTo?: Dayjs;
    senderId?: string;
    conversationType?: 'direct' | 'group';
    conversationIds?: string[];
  };
  timestamp: number;
}

const SEARCH_HISTORY_KEY = 'chat_search_history';
const MAX_HISTORY_ITEMS = 10;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FiltersSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

const FilterLabel = styled(Text)`
  min-width: 120px;
  font-weight: 500;
  padding-top: 6px;
`;

const HistorySection = styled.div`
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  padding: 8px;
  background-color: #fafafa;
`;

const HistoryItem = styled.div`
  padding: 8px;
  cursor: pointer;
  border-radius: 4px;
  margin-bottom: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #e6f7ff;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const HistoryQuery = styled(Text)`
  font-weight: 500;
  color: #1890ff;
`;

const HistoryMeta = styled(Text)`
  font-size: 12px;
  color: #999;
  margin-left: 8px;
`;

export const AdvancedSearchModal: React.FC<AdvancedSearchModalProps> = ({
  open,
  onClose,
  currentUserId,
  scope,
  municipalityId,
  conversations,
  onJumpToMessage,
}) => {
  const { t } = useTranslation('chat');
  const { message } = App.useApp();
  const [form] = Form.useForm<SearchFilters>();
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<Array<Message & { conversationName?: string; conversationType?: 'direct' | 'group' }>>([]);
  const [showResults, setShowResults] = useState(false);
  const [searchHistory, setSearchHistory] = useState<SearchHistoryItem[]>([]);

  // Load search history from localStorage
  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      const stored = localStorage.getItem(SEARCH_HISTORY_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as SearchHistoryItem[];
        setSearchHistory(parsed.slice(0, MAX_HISTORY_ITEMS));
      }
    } catch (error) {
      console.warn('Failed to load search history:', error);
    }
  }, []);

  // Get available senders from conversations
  const availableSenders = useMemo(() => {
    const senderMap = new Map<string, { id: string; name: string }>();

    conversations.forEach((conv) => {
      Object.entries(conv.participantDetails).forEach(([userId, details]) => {
        if (userId !== currentUserId && details.name) {
          senderMap.set(userId, {
            id: userId,
            name: details.name,
          });
        }
      });
    });

    return Array.from(senderMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [conversations, currentUserId]);

  // Get available conversations for filter
  const availableConversations = useMemo(() => {
    return conversations
      .filter((conv) => !conv.deletedBy.includes(currentUserId))
      .map((conv) => ({
        id: conv.id,
        name: conv.name || Object.values(conv.participantDetails).find((p) => p.name)?.name || 'Chat',
        type: conv.type,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [conversations, currentUserId]);

  // Save search to history
  const saveToHistory = useCallback((query: string, filters: SearchHistoryItem['filters']) => {
    if (!query.trim()) return;

    try {
      const historyItem: SearchHistoryItem = {
        query,
        filters,
        timestamp: Date.now(),
      };

      const updated = [historyItem, ...searchHistory.filter((item) => item.query !== query)]
        .slice(0, MAX_HISTORY_ITEMS);

      setSearchHistory(updated);
      localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(updated));
    } catch (error) {
      console.warn('Failed to save search history:', error);
    }
  }, [searchHistory]);

  // Load search from history
  const loadFromHistory = useCallback((item: SearchHistoryItem) => {
    const dateRange: [Dayjs, Dayjs] | undefined =
      item.filters.dateFrom && item.filters.dateTo
        ? [dayjs(item.filters.dateFrom), dayjs(item.filters.dateTo)]
        : undefined;

    form.setFieldsValue({
      query: item.query,
      dateRange,
      senderId: item.filters.senderId,
      conversationType: item.filters.conversationType,
      conversationIds: item.filters.conversationIds,
    });
  }, [form]);

  // Perform search
  const handleSearch = useCallback(async () => {
    try {
      const values = await form.validateFields();
      setIsSearching(true);
      setShowResults(false);

      // Handle date range from RangePicker
      const dateRange = values.dateRange as [Dayjs, Dayjs] | undefined;

      const filters = {
        dateFrom: dateRange?.[0]?.toDate(),
        dateTo: dateRange?.[1]?.toDate(),
        senderId: values.senderId,
        conversationType: values.conversationType,
        scope,
        municipalityId,
        conversationIds: values.conversationIds,
      };

      const results = await chatService.searchMessages(
        currentUserId,
        values.query || '',
        filters,
        100 // maxResults: Limit to 100 results
      );

      setSearchResults(results);
      setShowResults(true);

      // Save to history
      if (values.query) {
        saveToHistory(values.query, {
          dateFrom: dateRange?.[0],
          dateTo: dateRange?.[1],
          senderId: values.senderId,
          conversationType: values.conversationType,
          conversationIds: values.conversationIds,
        });
      }
    } catch (error) {
      console.error('Search error:', error);
      message.error(t('search.searchError') || 'Failed to perform search. Please try again.');
    } finally {
      setIsSearching(false);
    }
  }, [form, currentUserId, scope, municipalityId, saveToHistory, t, message]);

  // Clear search
  const handleClear = useCallback(() => {
    form.resetFields();
    setSearchResults([]);
    setShowResults(false);
  }, [form]);

  // Handle keyboard shortcuts
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      // Ctrl+F / Cmd+F - focus search input (already handled by browser, but we can prevent default)
      if (event.key === 'f' && (event.ctrlKey || event.metaKey)) {
        // Don't prevent default - let browser handle it
        return;
      }

      // Enter - perform search
      if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        handleSearch();
      }

      // Escape - close modal
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown as never);
    return () => document.removeEventListener('keydown', handleKeyDown as never);
  }, [open, handleSearch, onClose]);

  // Reset form and results when modal opens
  useEffect(() => {
    if (open) {
      // Reset form when modal opens (fresh start)
      form.resetFields();
      setSearchResults([]);
      setShowResults(false);
    }
  }, [open, form]);

  return (
    <Modal
      title={
        <Space>
          <SearchOutlined />
          <span>{t('search.title') || 'Advanced Message Search'}</span>
        </Space>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      destroyOnHidden
    >
      <ModalContent>
        <Form form={form} layout="vertical" onFinish={handleSearch}>
          {/* Search Query */}
          <Form.Item
            name="query"
            label={t('search.queryLabel') || 'Search Messages'}
            rules={[
              {
                validator: (_, value) => {
                  if (!value || value.trim().length === 0) {
                    return Promise.reject(new Error(t('search.queryRequired') || 'Please enter a search query'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              placeholder={t('search.queryPlaceholder') || 'Type to search messages...'}
              prefix={<SearchOutlined />}
              allowClear
              autoFocus
              onPressEnter={(e) => {
                e.preventDefault();
                handleSearch();
              }}
            />
          </Form.Item>

          {/* Filters Section */}
          <Divider orientation="left">
            <FilterOutlined /> {t('search.filters') || 'Filters'}
          </Divider>

          <FiltersSection>
            {/* Date Range */}
            <FilterRow>
              <FilterLabel>
                <CalendarOutlined /> {t('search.dateRange') || 'Date Range'}
              </FilterLabel>
              <Form.Item name={['dateRange']} style={{ margin: 0, flex: 1 }}>
                <RangePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  placeholder={[t('search.fromDate') || 'From', t('search.toDate') || 'To']}
                />
              </Form.Item>
            </FilterRow>

            {/* Sender */}
            {availableSenders.length > 0 && (
              <FilterRow>
                <FilterLabel>
                  <UserOutlined /> {t('search.sender') || 'Sender'}
                </FilterLabel>
                <Form.Item name="senderId" style={{ margin: 0, flex: 1 }}>
                  <Select
                    placeholder={t('search.senderPlaceholder') || 'Select sender...'}
                    allowClear
                    showSearch
                    filterOption={(input, option) => {
                      const children = option?.children;
                      const searchText = input.toLowerCase();
                      // Convert children to string for comparison
                      const text = String(children || '').toLowerCase();
                      return text.includes(searchText);
                    }}
                  >
                    {availableSenders.map((sender) => (
                      <Option key={sender.id} value={sender.id}>
                        {sender.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </FilterRow>
            )}

            {/* Conversation Type */}
            <FilterRow>
              <FilterLabel>
                <MessageOutlined /> {t('search.conversationType') || 'Conversation Type'}
              </FilterLabel>
              <Form.Item name="conversationType" style={{ margin: 0, flex: 1 }}>
                <Select placeholder={t('search.conversationTypePlaceholder') || 'All conversations'} allowClear>
                  <Option value="direct">
                    <UserOutlined /> {t('search.directChat') || 'Direct Chat'}
                  </Option>
                  <Option value="group">
                    <TeamOutlined /> {t('search.groupChat') || 'Group Chat'}
                  </Option>
                </Select>
              </Form.Item>
            </FilterRow>

            {/* Specific Conversations */}
            {availableConversations.length > 0 && (
              <FilterRow>
                <FilterLabel>
                  <TeamOutlined /> {t('search.conversations') || 'Conversations'}
                </FilterLabel>
                <Form.Item name="conversationIds" style={{ margin: 0, flex: 1 }}>
                  <Select
                    mode="multiple"
                    placeholder={t('search.conversationsPlaceholder') || 'Select conversations (optional)...'}
                    allowClear
                    showSearch
                    filterOption={(input, option) => {
                      const children = option?.children;
                      const searchText = input.toLowerCase();
                      // Convert children to string for comparison
                      const text = String(children || '').toLowerCase();
                      return text.includes(searchText);
                    }}
                  >
                    {availableConversations.map((conv) => (
                      <Option key={conv.id} value={conv.id}>
                        {conv.type === 'group' ? <TeamOutlined /> : <UserOutlined />} {conv.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </FilterRow>
            )}
          </FiltersSection>

          {/* Search History */}
          {searchHistory.length > 0 && !showResults && (
            <>
              <Divider orientation="left">
                <HistoryOutlined /> {t('search.recentSearches') || 'Recent Searches'}
              </Divider>
              <HistorySection>
                {searchHistory.map((item, index) => (
                  <HistoryItem
                    key={index}
                    onClick={() => {
                      loadFromHistory(item);
                      handleSearch();
                    }}
                  >
                    <HistoryQuery>{item.query}</HistoryQuery>
                    <HistoryMeta>
                      {dayjs(item.timestamp).format('DD/MM/YYYY HH:mm')}
                    </HistoryMeta>
                  </HistoryItem>
                ))}
              </HistorySection>
            </>
          )}

          {/* Action Buttons */}
          <Space style={{ marginTop: 16, justifyContent: 'flex-end', display: 'flex' }}>
            <Button icon={<ClearOutlined />} onClick={handleClear}>
              {t('search.clear') || 'Clear'}
            </Button>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              loading={isSearching}
            >
              {t('search.search') || 'Search'}
            </Button>
          </Space>
        </Form>

        {/* Search Results */}
        {showResults && (
          <>
            <Divider />
            {isSearching ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Spin size="large" />
                <div style={{ marginTop: 16 }}>
                  <Text type="secondary">{t('search.searching') || 'Searching...'}</Text>
                </div>
              </div>
            ) : searchResults.length === 0 ? (
              <Empty
                description={t('search.noResults') || 'No messages found matching your search criteria.'}
              />
            ) : (
              <SearchResults
                results={searchResults}
                searchQuery={form.getFieldValue('query') || ''}
                onJumpToMessage={onJumpToMessage}
                onClose={onClose}
              />
            )}
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

